import {
  ActionIcon,
  Box,
  Button,
  ColorPicker,
  ColorSwatch,
  FloatingIndicator,
  Grid,
  Group,
  Image,
  Paper,
  Popover,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import type { FileWithPath } from '@mantine/dropzone'
import { useEyeDropper } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconColorPicker, IconPhoto } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FileUpload } from '@/components/FileUpload'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetCurrentSponsorPlaceholdersResponse, SaveBrandingParams } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import * as cmClasses from '@/styles/ContentModuleFormScreen.css'

import { ConfirmChangesModal } from './ConfirmChangesModal'

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const BRAND_TITLE_MAX = 25
const BRAND_SUBTITLE_MAX = 30
const CONTROL_CHAR_RE = /[\t\n\r]/

function validateBrandTitle(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'Channel name is required.'
  if (trimmed.length > BRAND_TITLE_MAX)
    return `Channel name is too long. Please keep it under ${BRAND_TITLE_MAX} characters.`
  if (CONTROL_CHAR_RE.test(value))
    return 'Text contains unsupported characters. Please remove line breaks or control characters.'
  return null
}

function validateBrandSubtitle(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.length > BRAND_SUBTITLE_MAX)
    return `Subtitle is too long. Please keep it under ${BRAND_SUBTITLE_MAX} characters.`
  if (CONTROL_CHAR_RE.test(value))
    return 'Text contains unsupported characters. Please remove line breaks or control characters.'
  return null
}

/**
 * Loads a File into an HTMLImageElement and checks exact dimensions.
 * Resolves with null on success or an error message string on failure.
 */
function validateImageDimensions(file: File): Promise<string | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new window.Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.naturalWidth !== 1024 || img.naturalHeight !== 1024) {
        resolve('Your image does not have the correct dimensions. Please upload one that is exactly 1024 \u00d7 1024.')
      }
      resolve(null)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve('The image appears to be corrupted or could not be read.')
    }
    img.src = url
  })
}

const SKELETON_BUBBLES: Array<{ side: 'left' | 'right'; width: string; height: number }> = [
  { side: 'left', width: '65%', height: 44 },
  { side: 'right', width: '50%', height: 22 },
  { side: 'left', width: '75%', height: 56 },
  { side: 'right', width: '40%', height: 22 },
  { side: 'left', width: '55%', height: 36 },
  { side: 'right', width: '60%', height: 30 },
  { side: 'left', width: '45%', height: 44 },
]

// Curated swatches for the color picker
const COLOR_SWATCHES = [
  '#FFFFFF',
  '#000000',
  '#1A1B1E',
  '#25262B',
  '#868E96',
  '#FA5252',
  '#E64980',
  '#BE4BDB',
  '#7950F2',
  '#4C6EF5',
  '#228BE6',
  '#15AABF',
  '#12B886',
  '#40C057',
  '#82C91E',
  '#FAB005',
  '#FD7E14',
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BrandingPanelProps {
  // Tab key is 'terminology' for backward compat with SettingsTabKey -- the
  // tab was renamed to "Branding" but the routing key was kept stable.
  registerUnsavedChangesChecker: (tabKey: 'terminology', checker: () => boolean, onDiscard: () => void) => void
}

interface BrandState {
  brandTitle: string
  brandSubtitle: string
  brandColor: string
  // S3 key for a newly-uploaded image (set after successful upload)
  pendingImageS3Key: string | null
  // URL for preview (either CloudFront URL from API or local object URL)
  imagePreviewUrl: string | null
  // Whether the user explicitly removed the image
  imageRemoved: boolean
  // File metadata for the uploaded-image preview row
  imageFileName: string | null
  imageFileSize: number | null
}

interface TermState {
  sponsorNameShort: string
  userTypeName: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BrandingPanel({ registerUnsavedChangesChecker }: BrandingPanelProps) {
  const { api, configStore } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const { supported: eyeDropperSupported, open: openEyeDropper } = useEyeDropper()

  // Track locally-created object URLs so we can revoke them on cleanup
  const localPreviewUrlRef = useRef<string | null>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const termsInitializedRef = useRef(false)
  const brandInitializedRef = useRef(false)

  useEffect(() => {
    return () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current)
      }
    }
  }, [])

  // Preview tab state (Tabs + FloatingIndicator pattern)
  type PreviewTab = 'splash' | 'banner'
  const [previewTab, setPreviewTab] = useState<PreviewTab>('splash')
  const [previewTabsRootRef, setPreviewTabsRootRef] = useState<HTMLDivElement | null>(null)
  const [previewTabControlsRefs, setPreviewTabControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({})
  const setPreviewTabControlRef = useMemo(() => {
    const cache: Record<string, (node: HTMLButtonElement | null) => void> = {}
    return (val: string) => {
      if (!cache[val]) {
        cache[val] = (node: HTMLButtonElement | null) => {
          setPreviewTabControlsRefs(prev => (prev[val] === node ? prev : { ...prev, [val]: node }))
        }
      }
      return cache[val]
    }
  }, [])

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const terminologyQuery = useQuery({
    queryKey: ['terminology', 'placeholder'],
    queryFn: () => api.getPlaceholderTerminology(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  // -------------------------------------------------------------------------
  // Local state
  // -------------------------------------------------------------------------

  const [originalTerms, setOriginalTerms] = useState<TermState | null>(null)
  const [terms, setTerms] = useState<TermState | null>(null)

  const [originalBrand, setOriginalBrand] = useState<BrandState | null>(null)
  const [brand, setBrand] = useState<BrandState | null>(null)

  // Sync terminology query -> local state (once on initial load)
  useEffect(() => {
    if (terminologyQuery.data && !termsInitializedRef.current) {
      termsInitializedRef.current = true
      const t: TermState = {
        sponsorNameShort: terminologyQuery.data.sponsorNameShort ?? '',
        userTypeName: terminologyQuery.data.userTypeName ?? '',
      }
      setOriginalTerms(t)
      setTerms(t)
    }
  }, [terminologyQuery.data])

  // Sync sponsor info (which includes brand fields) -> local brand state.
  // Falls back to terminology-derived defaults when brand fields are null.
  // Only runs once; post-save state is updated manually in handleConfirmApply.
  useEffect(() => {
    if (sponsorInfoQuery.data && terminologyQuery.data && !brandInitializedRef.current) {
      brandInitializedRef.current = true
      const d = sponsorInfoQuery.data
      const b: BrandState = {
        brandTitle: d.brandTitle ?? terminologyQuery.data?.sponsorName ?? d.name ?? '',
        brandSubtitle:
          d.brandSubtitle ??
          (terminologyQuery.data?.userTypeName ? `Supporting ${terminologyQuery.data.userTypeName}s` : ''),
        brandColor: d.brandColor ?? '#FFFFFF',
        pendingImageS3Key: null,
        imagePreviewUrl: d.brandImage ? `${configStore.serverBaseUrl}/files/${d.brandImage}` : null,
        imageRemoved: false,
        imageFileName: d.brandImage ? 'Brand Image' : null,
        imageFileSize: null,
      }
      setOriginalBrand(b)
      setBrand(b)
    }
  }, [sponsorInfoQuery.data, terminologyQuery.data, configStore.serverBaseUrl])

  // -------------------------------------------------------------------------
  // Validation errors (inline)
  // -------------------------------------------------------------------------

  const titleError = brand ? validateBrandTitle(brand.brandTitle) : null
  const subtitleError = brand ? validateBrandSubtitle(brand.brandSubtitle) : null

  // -------------------------------------------------------------------------
  // Change detection
  // -------------------------------------------------------------------------

  const hasTermChanges = useMemo(() => {
    if (!originalTerms || !terms) return false
    return (
      originalTerms.sponsorNameShort !== terms.sponsorNameShort || originalTerms.userTypeName !== terms.userTypeName
    )
  }, [originalTerms, terms])

  const hasBrandChanges = useMemo(() => {
    if (!originalBrand || !brand) return false
    return (
      originalBrand.brandTitle !== brand.brandTitle ||
      originalBrand.brandSubtitle !== brand.brandSubtitle ||
      originalBrand.brandColor.toLowerCase() !== brand.brandColor.toLowerCase() ||
      brand.pendingImageS3Key !== null ||
      brand.imageRemoved
    )
  }, [originalBrand, brand])

  const hasChanges = hasTermChanges || hasBrandChanges

  const isValid = useMemo(() => {
    if (!terms || !brand) return false
    if (terms.sponsorNameShort.trim() === '') return false
    if (terms.userTypeName.trim() === '') return false
    if (validateBrandTitle(brand.brandTitle) !== null) return false
    if (validateBrandSubtitle(brand.brandSubtitle) !== null) return false
    return true
  }, [terms, brand])

  // -------------------------------------------------------------------------
  // Unsaved changes registration
  // -------------------------------------------------------------------------

  const handleCancel = useCallback(() => {
    setTerms(originalTerms)
    setBrand(originalBrand)
    // Revoke any locally-created object URL
    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current)
      localPreviewUrlRef.current = null
    }
  }, [originalTerms, originalBrand])

  useEffect(() => {
    registerUnsavedChangesChecker('terminology', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  // -------------------------------------------------------------------------
  // Image upload
  // -------------------------------------------------------------------------

  const { upload: uploadFile, uploading, uploadProgress } = useFileUpload()

  const handleImageDrop = useCallback(
    async (files: FileWithPath[]) => {
      const file = files[0]
      if (!file) return

      // Validate MIME type
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        notifications.show({
          title: 'Invalid File Type',
          message: 'Your image does not have the right file type. Please upload a PNG or JPEG.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        return
      }

      // Validate dimensions
      const dimError = await validateImageDimensions(file)
      if (dimError) {
        notifications.show({
          title: 'Invalid Dimensions',
          message: dimError,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        return
      }

      const result = await uploadFile(file)
      if (!result) return

      // Revoke previous local preview
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current)
      }
      const previewUrl = URL.createObjectURL(file)
      localPreviewUrlRef.current = previewUrl

      const s3FileKey = result.filePath.split('?')[0]
      setBrand(prev => {
        if (!prev) return prev
        return {
          ...prev,
          pendingImageS3Key: s3FileKey,
          imagePreviewUrl: previewUrl,
          imageRemoved: false,
          imageFileName: file.name,
          imageFileSize: file.size,
        }
      })
    },
    [uploadFile],
  )

  const handleImageRemove = useCallback(() => {
    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current)
      localPreviewUrlRef.current = null
    }
    setBrand(prev => {
      if (!prev) return prev
      return {
        ...prev,
        pendingImageS3Key: null,
        imagePreviewUrl: null,
        imageRemoved: true,
        imageFileName: null,
        imageFileSize: null,
      }
    })
  }, [])

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------

  const handleSave = () => {
    if (!isValid) return
    setShowConfirmModal(true)
  }

  const handleConfirmApply = async () => {
    if (!terms || !brand) return

    setIsPending(true)
    try {
      // Build a single payload for both brand and terminology fields.
      // We sync brand.brandTitle -> sponsorName so that sidekick prompts
      // referencing {sponsor_name} continue to work. brand_title is the
      // canonical channel name; sponsorName is kept as a legacy alias.
      const termPayload: Partial<GetCurrentSponsorPlaceholdersResponse> = {
        sponsorNameShort: terms.sponsorNameShort,
        userTypeName: terms.userTypeName,
        sponsorName: brand.brandTitle,
      }

      if (terminologyQuery.data) {
        termPayload.sponsorCrisisInstruction = terminologyQuery.data.sponsorCrisisInstruction
        termPayload.memberIntroduction = terminologyQuery.data.memberIntroduction
        termPayload.smeIntroduction = terminologyQuery.data.smeIntroduction
        termPayload.sponsorAdminIntroduction = terminologyQuery.data.sponsorAdminIntroduction
      }

      const params: SaveBrandingParams = {
        brand: {
          brand_title: brand.brandTitle,
          brand_subtitle: brand.brandSubtitle,
          brand_color: brand.brandColor,
          ...(brand.pendingImageS3Key !== null ? { brand_image: brand.pendingImageS3Key } : {}),
          ...(brand.imageRemoved ? { brand_image: null } : {}),
        },
        terminology: termPayload,
      }

      await api.saveBranding(params)

      await Promise.all([
        api.queryClient.invalidateQueries({ queryKey: ['terminology', 'placeholder'] }),
        api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'info'] }),
      ])

      setOriginalTerms({ ...terms })
      const savedBrand = {
        ...brand,
        pendingImageS3Key: null,
        imageRemoved: false,
      }
      setOriginalBrand(savedBrand)
      setBrand(savedBrand)

      notifications.show({
        title: 'Branding Updated',
        message: 'Your channel branding has been saved.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    } catch (error) {
      console.error('Failed to save branding:', error)
      notifications.show({
        title: 'Save Failed',
        message: 'Could not save branding changes. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    } finally {
      setIsPending(false)
      setShowConfirmModal(false)
    }
  }

  // -------------------------------------------------------------------------
  // Field change handlers
  // -------------------------------------------------------------------------

  const handleTermChange = (key: keyof TermState, value: string) => {
    setTerms(prev => (prev ? { ...prev, [key]: value } : prev))
  }

  const handleBrandChange = (
    key: keyof Pick<BrandState, 'brandTitle' | 'brandSubtitle' | 'brandColor'>,
    value: string,
  ) => {
    setBrand(prev => (prev ? { ...prev, [key]: value } : prev))
  }

  const handleHexInput = (raw: string) => {
    const v = raw.startsWith('#') ? raw : `#${raw}`
    if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) {
      handleBrandChange('brandColor', v)
    }
  }

  const handleEyeDropper = async () => {
    try {
      const result = await openEyeDropper()
      if (result?.sRGBHex) handleBrandChange('brandColor', result.sRGBHex)
    } catch {
      // user cancelled
    }
  }

  // -------------------------------------------------------------------------
  // Loading / error states
  // -------------------------------------------------------------------------

  const isLoading = terminologyQuery.isLoading || sponsorInfoQuery.isLoading
  const anyError = terminologyQuery.isError || sponsorInfoQuery.isError

  if (isLoading || anyError) {
    return (
      <Stack>
        <Paper radius="md" p="lg" shadow="sm" px="xl" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between" align="center" p="md">
            <Title order={4}>Define Channel Branding</Title>
            {isLoading && <Text>Loading...</Text>}
            {anyError && <Text>Error loading data</Text>}
          </Group>
        </Paper>
      </Stack>
    )
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" mb="md">
          <Title order={isMobile ? 6 : 4}>Define Channel Branding</Title>
          <Group>
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Tooltip label="Save becomes available when you make changes.">
              <Button onClick={handleSave} disabled={!isValid || !hasChanges} loading={isPending}>
                Save
              </Button>
            </Tooltip>
          </Group>
        </Group>

        {/* ---- Channel Identity ---- */}
        <Paper radius="md" p="lg" withBorder mb="lg">
          <Title order={6} mb="md">
            Channel Identity
          </Title>
          <Grid>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="Channel Name"
                description="The primary display name for your channel (max 25 characters)."
                required
                value={brand?.brandTitle ?? ''}
                onChange={e => handleBrandChange('brandTitle', e.target.value)}
                maxLength={BRAND_TITLE_MAX}
                error={brand?.brandTitle !== originalBrand?.brandTitle ? titleError : undefined}
                disabled={isPending}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="Subtitle"
                description="A short subtitle shown below the channel name (max 30 characters)."
                value={brand?.brandSubtitle ?? ''}
                onChange={e => handleBrandChange('brandSubtitle', e.target.value)}
                maxLength={BRAND_SUBTITLE_MAX}
                error={brand?.brandSubtitle !== originalBrand?.brandSubtitle ? subtitleError : undefined}
                disabled={isPending}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="Channel Short Name"
                description="An abbreviated name or nickname for your channel."
                required
                value={terms?.sponsorNameShort ?? ''}
                onChange={e => handleTermChange('sponsorNameShort', e.target.value)}
                disabled={isPending}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="End User Role"
                description="How sidekicks refer to your end users."
                required
                value={terms?.userTypeName ?? ''}
                onChange={e => handleTermChange('userTypeName', e.target.value)}
                disabled={isPending}
              />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* ---- App Icon ---- */}
        <Paper radius="md" p="lg" withBorder mb="lg">
          <Title order={6} mb={4}>
            Channel Icon
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Upload a square image for the splash screen and banner.
          </Text>
          {brand?.imagePreviewUrl ? (
            <Paper withBorder p="sm" radius="md" shadow="0">
              <Group gap="md" align="center" wrap="nowrap">
                <Box
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid var(--mantine-color-gray-3)',
                    flexShrink: 0,
                  }}>
                  <Image src={brand.imagePreviewUrl} alt="Brand image preview" w={96} h={96} fit="cover" />
                </Box>
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {brand.imageFileName ?? 'Brand Image'}
                  </Text>
                  {brand.imageFileSize != null && (
                    <Text size="xs" c="dimmed">
                      {formatFileSize(brand.imageFileSize)}
                    </Text>
                  )}
                </Stack>
                <input
                  ref={replaceInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) handleImageDrop([file as FileWithPath])
                    e.target.value = ''
                  }}
                />
                <Group gap="xs" style={{ flexShrink: 0 }}>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => replaceInputRef.current?.click()}
                    disabled={isPending || uploading}>
                    Replace
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    color="red"
                    onClick={handleImageRemove}
                    disabled={isPending || uploading}>
                    Remove
                  </Button>
                </Group>
              </Group>
            </Paper>
          ) : (
            <FileUpload
              accept={['image/png', 'image/jpeg']}
              maxFiles={1}
              onDrop={handleImageDrop}
              uploadPhase={uploading ? 'uploading' : 'idle'}
              uploadProgress={uploadProgress}
              disabled={isPending}
              icons={{ idle: IconPhoto }}
              titles={{ idle: 'Drag & drop your icon here, or browse' }}
              hints={{
                idle: 'PNG or JPG, size 1024 \u00d7 1024px required',
                reject: 'Only PNG and JPEG images are supported.',
              }}
              primaryActionLabel="Browse"
              actionHidden
            />
          )}
        </Paper>

        {/* ---- Splash Background ---- */}
        <Paper radius="md" p="lg" withBorder mb="lg">
          <Title order={6} mb={4}>
            Splash Background
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Choose a background color for the app&apos;s splash screen.
          </Text>
          <Group gap="sm" align="center">
            <Popover
              opened={colorPickerOpen}
              onChange={setColorPickerOpen}
              position="bottom-start"
              shadow="md"
              withArrow>
              <Popover.Target>
                <ColorSwatch
                  color={brand?.brandColor ?? '#FFFFFF'}
                  radius="md"
                  size={42}
                  onClick={() => setColorPickerOpen(o => !o)}
                  style={{ cursor: 'pointer', border: '1px solid var(--mantine-color-gray-3)' }}
                />
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap="sm">
                  <ColorPicker
                    format="hex"
                    value={brand?.brandColor ?? '#FFFFFF'}
                    onChange={val => handleBrandChange('brandColor', val)}
                    swatches={COLOR_SWATCHES}
                    swatchesPerRow={9}
                  />
                </Stack>
              </Popover.Dropdown>
            </Popover>

            {eyeDropperSupported && (
              <Tooltip label="Pick color from screen">
                <ActionIcon variant="default" size="input-md" onClick={handleEyeDropper} disabled={isPending}>
                  <IconColorPicker size={20} />
                </ActionIcon>
              </Tooltip>
            )}

            <TextInput
              value={brand?.brandColor ?? '#FFFFFF'}
              onChange={e => handleHexInput(e.currentTarget.value)}
              disabled={isPending}
              size="md"
              style={{ width: 130 }}
              styles={{ input: { fontFamily: 'monospace' } }}
            />
          </Group>
        </Paper>

        {/* ---- Preview ---- */}
        <Paper radius="md" p="lg" withBorder>
          <Title order={6} mb="md">
            Preview
          </Title>

          <Tabs variant="none" value={previewTab} onChange={v => v && setPreviewTab(v as PreviewTab)}>
            <Tabs.List ref={setPreviewTabsRootRef} className={cmClasses.contentTabsList}>
              <Tabs.Tab
                value="splash"
                ref={setPreviewTabControlRef('splash')}
                classNames={cmClasses.contentTabClassNames}>
                Splash Screen
              </Tabs.Tab>
              <Tabs.Tab
                value="banner"
                ref={setPreviewTabControlRef('banner')}
                classNames={cmClasses.contentTabClassNames}>
                App Banner
              </Tabs.Tab>
              <FloatingIndicator
                target={previewTab ? previewTabControlsRefs[previewTab] : null}
                parent={previewTabsRootRef}
                className={cmClasses.contentTabIndicator}
              />
            </Tabs.List>

            <Tabs.Panel value="splash">
              <Stack align="center">
                <Box
                  style={{
                    backgroundColor: brand?.brandColor ?? '#FFFFFF',
                    border: '1px solid var(--mantine-color-gray-3)',
                    borderRadius: 24,
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    aspectRatio: '9 / 16',
                    maxHeight: 480,
                    width: '100%',
                    maxWidth: 270,
                    gap: 16,
                  }}>
                  <Box style={{ flex: 1 }} />
                  {brand?.imagePreviewUrl && (
                    <Box
                      style={{
                        width: 128,
                        height: 128,
                        borderRadius: 8,
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}>
                      <Image src={brand.imagePreviewUrl} alt="Splash preview" w={128} h={128} fit="contain" />
                    </Box>
                  )}
                  <Stack gap={2} align="center">
                    <Text
                      fw={700}
                      size="md"
                      ta="center"
                      style={{ color: isLightColor(brand?.brandColor) ? '#000' : '#FFF' }}>
                      {brand?.brandTitle || 'Channel Name'}
                    </Text>
                    {brand?.brandSubtitle ? (
                      <Text size="xs" ta="center" style={{ color: isLightColor(brand?.brandColor) ? '#555' : '#DDD' }}>
                        {brand.brandSubtitle}
                      </Text>
                    ) : null}
                  </Stack>
                  <Box style={{ flex: 1 }} />
                  <Text
                    fz={10}
                    ta="center"
                    style={{
                      color: isLightColor(brand?.brandColor) ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
                      letterSpacing: 2,
                    }}>
                    Loading &bull;&bull;&bull;
                  </Text>
                </Box>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="banner">
              <Stack align="center">
                <Box
                  style={{
                    border: '1px solid var(--mantine-color-gray-3)',
                    borderRadius: 24,
                    aspectRatio: '9 / 16',
                    maxHeight: 480,
                    width: '100%',
                    maxWidth: 270,
                    backgroundColor: 'var(--mantine-color-gray-0)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    overflow: 'hidden',
                    position: 'relative',
                  }}>
                  {/* Status bar spacer */}
                  <Box style={{ height: 24, flexShrink: 0 }} />

                  {/* Banner pill */}
                  <Box
                    style={{
                      borderRadius: 999,
                      padding: '6px 12px',
                      margin: '0 10px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: brand?.imagePreviewUrl ? 'flex-start' : 'center',
                      backgroundColor: 'var(--mantine-color-body)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      zIndex: 1,
                    }}>
                    {brand?.imagePreviewUrl && (
                      <Box
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}>
                        <Image src={brand.imagePreviewUrl} alt="Banner preview" w={30} h={30} fit="cover" />
                      </Box>
                    )}
                    <Stack gap={0}>
                      <Text
                        fw={700}
                        fz={10}
                        tt="uppercase"
                        style={brand?.imagePreviewUrl ? undefined : { textAlign: 'center' }}>
                        {brand?.brandTitle || 'Channel Name'}
                      </Text>
                      {brand?.brandSubtitle ? (
                        <Text
                          fz={8}
                          c="dimmed"
                          tt="uppercase"
                          style={brand?.imagePreviewUrl ? undefined : { textAlign: 'center' }}>
                          {brand.brandSubtitle}
                        </Text>
                      ) : null}
                    </Stack>
                  </Box>

                  {/* Skeleton chat bubbles */}
                  <Box style={{ flex: 1, padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {SKELETON_BUBBLES.map((bubble, i) => (
                      <Box
                        key={i}
                        style={{
                          alignSelf: bubble.side === 'left' ? 'flex-start' : 'flex-end',
                          width: bubble.width,
                          height: bubble.height,
                          borderRadius: 12,
                          backgroundColor:
                            bubble.side === 'left' ? 'var(--mantine-color-gray-2)' : 'var(--mantine-color-gray-3)',
                        }}
                      />
                    ))}
                  </Box>

                  {/* Bottom nav island */}
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 14,
                      right: 14,
                      display: 'flex',
                      gap: 8,
                      backgroundColor: 'var(--mantine-color-body)',
                      borderRadius: 999,
                      padding: '10px 14px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                    }}>
                    <Box
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-gray-3)',
                      }}
                    />
                    <Box
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-gray-4)',
                      }}
                    />
                    <Box
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-gray-3)',
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Paper>

      <ConfirmChangesModal
        title="Save Branding Changes?"
        description="Updating branding will change how your channel appears to end users in the mobile app."
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        isLoading={isPending}
      />
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function isLightColor(hex: string | undefined): boolean {
  if (!hex) return true
  const c = hex.replace('#', '')
  if (c.length < 6) return true
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  // Relative luminance approximation
  return (r * 299 + g * 587 + b * 114) / 1000 > 128
}
